import React, { useState } from "react";
import { toast } from "react-toastify";

import {
  ADJUSTMENT_REASON_LABELS,
  AdjustmentReason,
  AdjustmentType,
  createAdjustmentApi,
  DP_ADJUSTMENT_REASONS,
  DpPunch,
  hhmm,
  timeOf,
  TimesheetDay,
  uploadAdjustmentFileApi,
} from "../api/timekeepingApi";

export type TimeAdjustmentModalMode = Exclude<AdjustmentType, "ANULACAO">;

type Props = {
  token: string;
  contractId: string;
  day: TimesheetDay;
  mode: TimeAdjustmentModalMode;
  /** DESCONSIDERAR: a marcação original escolhida. */
  punch?: DpPunch;
  /** INCLUSAO: horário sugerido (minuto do dia). */
  suggestedMinute?: number | null;
  onClose: () => void;
  onSaved: () => void;
};

const TITLES: Record<TimeAdjustmentModalMode, string> = {
  INCLUSAO_MARCACAO: "Incluir marcação",
  DESCONSIDERAR_MARCACAO: "Desconsiderar marcação",
  ABONO_FALTA: "Abonar falta",
  ABONO_ATRASO: "Abonar atraso",
  ABONO_HORAS: "Abonar horas",
};

const toMinute = (value: string) => {
  const m = /^(\d{2}):(\d{2})$/.exec(value);
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};

/**
 * Lançamento do DP no espelho: nasce aprovado, com motivo e, se quiser, um
 * anexo. A marcação original nunca é alterada — desconsiderar só tira ela da
 * conta, e anular o ajuste devolve.
 */
export default function TimeAdjustmentModal({ token, contractId, day, mode, punch, suggestedMinute, onClose, onSaved }: Props) {
  const [time, setTime] = useState(hhmm(suggestedMinute ?? day.startMinute ?? 480));
  const [minutes, setMinutes] = useState(String(mode === "ABONO_ATRASO" ? day.lateMinutes + day.earlyLeaveMinutes || 10 : 30));
  const [reasonCode, setReasonCode] = useState<AdjustmentReason>(
    mode === "DESCONSIDERAR_MARCACAO" ? "MARCACAO_INDEVIDA" : mode.startsWith("ABONO") ? "AUSENCIA_JUSTIFICADA" : "ESQUECIMENTO",
  );
  const [reason, setReason] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);

  const save = async (e: React.FormEvent) => {
    e.preventDefault();
    if (reasonCode === "OUTRO" && reason.trim().length < 10) return toast.error("Com o motivo \"Outro\", descreva o que aconteceu (mínimo de 10 caracteres).");
    setSaving(true);
    try {
      let attachment: { attachmentAssetId?: string; attachmentFileName?: string } = {};
      if (file) {
        const uploaded = await uploadAdjustmentFileApi(token, file);
        attachment = { attachmentAssetId: uploaded.assetId, attachmentFileName: uploaded.fileName ?? file.name };
      }
      let minuteOfDay: number | undefined;
      if (mode === "INCLUSAO_MARCACAO") {
        const minute = toMinute(time);
        if (minute === null) throw new Error("Informe o horário.");
        // Turno que vira o dia: horário antes do início −3h é da madrugada seguinte.
        minuteOfDay = day.startMinute !== null && day.endMinute !== null && day.endMinute > 1440 && minute < day.startMinute - 180 ? minute + 1440 : minute;
      }
      await createAdjustmentApi(token, contractId, {
        day: day.day.slice(0, 10),
        type: mode,
        reasonCode,
        reason: reason.trim() || undefined,
        minuteOfDay,
        minutes: mode === "ABONO_ATRASO" || mode === "ABONO_HORAS" ? Number(minutes) : undefined,
        targetPunchId: mode === "DESCONSIDERAR_MARCACAO" ? punch?.id : undefined,
        ...attachment,
      });
      toast.success("Ajuste lançado.");
      onSaved();
    } catch (error: any) {
      toast.error(error?.message || "Erro ao lançar o ajuste.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="dpModalOverlay">
      <form className="dpModal" onSubmit={save}>
        <header className="dpModal__header">
          <h2 className="dpModal__title">
            {TITLES[mode]} · {new Date(day.day).toLocaleDateString("pt-BR", { timeZone: "UTC", weekday: "short", day: "2-digit", month: "2-digit" })}
          </h2>
          <button type="button" className="dpModal__close" onClick={onClose} disabled={saving}>
            &times;
          </button>
        </header>
        <div className="dpModal__body">
          <p className="dpNotice">Lançamento do DP: entra aprovado e fica registrado com o seu nome. A marcação original do colaborador nunca é alterada.</p>

          {mode === "INCLUSAO_MARCACAO" ? (
            <div className="dpFormGroup">
              <label htmlFor="tkAdjTime">Horário *</label>
              <input id="tkAdjTime" type="time" className="dpInput" value={time} onChange={(e) => setTime(e.target.value)} required />
              {day.startMinute !== null ? (
                <span className="dpFormHint">
                  Previsto: {hhmm(day.startMinute)} às {hhmm(day.endMinute)}
                </span>
              ) : null}
            </div>
          ) : null}

          {mode === "DESCONSIDERAR_MARCACAO" && punch ? (
            <p>
              Marcação nº {punch.nsr} das <strong>{timeOf(punch.punchedAt)}</strong> deixa de contar no espelho.
            </p>
          ) : null}

          {mode === "ABONO_ATRASO" || mode === "ABONO_HORAS" ? (
            <div className="dpFormGroup">
              <label htmlFor="tkAdjMinutes">Minutos a abonar *</label>
              <input id="tkAdjMinutes" type="number" min={1} max={1440} className="dpInput" value={minutes} onChange={(e) => setMinutes(e.target.value)} required />
            </div>
          ) : null}

          {mode === "ABONO_FALTA" ? <p>A falta do dia deixa de ser descontada e não tira o descanso semanal.</p> : null}

          <div className="dpFormGroup">
            <label htmlFor="tkAdjReason">Motivo *</label>
            <select id="tkAdjReason" className="dpSelect" value={reasonCode} onChange={(e) => setReasonCode(e.target.value as AdjustmentReason)}>
              {DP_ADJUSTMENT_REASONS.map((r) => (
                <option key={r} value={r}>
                  {ADJUSTMENT_REASON_LABELS[r]}
                </option>
              ))}
            </select>
          </div>
          <div className="dpFormGroup">
            <label htmlFor="tkAdjText">Observação{reasonCode === "OUTRO" ? " *" : ""}</label>
            <textarea id="tkAdjText" className="dpTextarea" maxLength={1000} value={reason} onChange={(e) => setReason(e.target.value)} />
          </div>
          <div className="dpFormGroup">
            <label htmlFor="tkAdjFile">Anexo (opcional)</label>
            <input id="tkAdjFile" type="file" accept="application/pdf,image/jpeg,image/png,image/webp" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </div>
        </div>
        <footer className="dpModal__footer">
          <button type="button" className="dpBtn dpBtn--secondary" onClick={onClose} disabled={saving}>
            Cancelar
          </button>
          <button type="submit" className="dpBtn dpBtn--primary" disabled={saving}>
            {saving ? "Salvando..." : "Lançar"}
          </button>
        </footer>
      </form>
    </div>
  );
}
